// File: src/components/PhenixVideoPlayer.jsx
import React, { useEffect, useContext, useRef } from 'react';
import { ChannelContext } from './PhenixChannelContext';
import { tokenTargetsSameStream } from '../utils/tokenUtils';

function PhenixVideoPlayer({ phenix, token, onResolutionChange, onStateChange }) {
    const { setChannel, channelId } = useContext(ChannelContext);
    const videoRef = useRef(null);
    const channelRef = useRef(null);
    const lastTokenRef = useRef(null);

    // Create channel when channelId changes (or on mount). Recreate only when switching channel/stream.
    // Do NOT recreate on token refresh — use token-update effect instead.
    useEffect(() => {
        if (!videoRef.current) return;

        const newChannel = phenix.Channels.createChannel({
            videoElement: videoRef.current,
            token: token
        });
        channelRef.current = newChannel;
        lastTokenRef.current = token;
        setChannel(newChannel);

        const subscriptions = [];
        subscriptions.push(newChannel.autoMuted.subscribe(autoMuted => {
            if (autoMuted) {
                console.log('Playback was auto-muted by browser. Please use user action to initiate `channel.unmute()`');
            }
        }));
        subscriptions.push(newChannel.autoPaused.subscribe(autoPaused => {
            if (autoPaused) {
                console.log('Playback was auto-paused by browser. Please use user action to initiate `channel.play()`');
            }
        }));
        subscriptions.push(newChannel.resolution.subscribe(newResolution => {
            onResolutionChange?.(newResolution);
        }));
        if (onStateChange) {
            subscriptions.push(newChannel.state.subscribe(state => onStateChange(state)));
        }

        return () => {
            const ch = channelRef.current;
            if (ch) {
                channelRef.current = null;
                setChannel(null);
                subscriptions.forEach(s => { try { s.dispose(); } catch (_) { /* ignore */ } });
                ch.dispose().catch(err => console.warn(`Channel dispose error for ${channelId}:`, err));
            }
        };
    }, [channelId]);

    // Update token when it changes (e.g. refresh, or new token after Unauthorized).
    // channel.token = newToken kicks off start workflow; no need to dispose/recreate.
    // Only assign when channel/stream identity changed — token string changes every refresh
    // but same stream = skip update to avoid SDK reconnection and getStats errors.
    useEffect(() => {
        const ch = channelRef.current;
        const last = lastTokenRef.current;
        if (!ch || !token) return;
        if (last && tokenTargetsSameStream(last, token)) return;

        lastTokenRef.current = token;
        ch.token = token;
    }, [token]);

    return (
        <div className="relative w-full h-full flex items-center justify-center bg-black">
            <video 
                ref={videoRef} 
                controls 
                muted 
                autoPlay 
                playsInline
                className="absolute inset-0 m-auto max-w-full max-h-full object-contain"
            ></video>
        </div>
    );
}

export default PhenixVideoPlayer;